/**
 * CompoundTextInput - A custom widget for JSON Schema forms that displays multiple
 * text fields arranged in a two-column layout with a title.
 *
 * Usage in uiSchema:
 * {
 *   fieldName: {
 *     'ui:widget': 'CompoundTextInput',
 *     'ui:title': 'Contact Information',
 *     'ui:placeholder': 'Enter contact details',
 *     'ui:label': false, // Optional: set to false to hide the field label
 *     'ui:size': 'small', // Optional: 'small' | 'medium' (default) | 'large'
 *     'ui:options': {
 *       fields: [
 *         { name: 'firstName', placeholder: 'First Name', required: true },
 *         { name: 'lastName', placeholder: 'Last Name', required: true },
 *         { name: 'email', placeholder: 'Email Address', required: false },
 *         { name: 'phone', placeholder: 'Phone Number', required: false }
 *       ]
 *     }
 *   }
 * }
 *
 * Value format: { [fieldName]: string }
 */

import { useState, useEffect } from 'react';
import { WidgetProps } from '@rjsf/utils';
import { TextField, Box, Grid } from '@mui/material';
import { INPUT_STYLE } from '../../../../../utils/styleHelpers';
import { DetailRow } from '../../../DetailsDrawer/DetailRow';

interface CompoundField {
  name: string;
  placeholder: string;
  required?: boolean;
  type?: string; // For future extensibility (email, tel, etc.)
}

interface CompoundTextValue {
  [fieldName: string]: string;
}

export const CompoundTextInput = (props: WidgetProps) => {
  const { value: formValue, onChange, rawErrors } = props;
  const [dirty, setDirty] = useState(false);

  // Initialize values from form value or empty object
  const initialValue: CompoundTextValue = formValue || {};
  const [values, setValues] = useState<CompoundTextValue>(initialValue);

  // Get field configuration from uiSchema
  const fieldConfig = props.uiSchema?.['ui:options']?.fields || [];
  const fields: CompoundField[] = Array.isArray(fieldConfig) ? fieldConfig : [];

  // Initialize empty values for all fields if not present
  useEffect(() => {
    const initializedValues: CompoundTextValue = { ...values };
    let hasChanges = false;

    fields.forEach((field) => {
      if (!(field.name in initializedValues)) {
        initializedValues[field.name] = '';
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setValues(initializedValues);

      // Check validation for the initialized values
      const hasRequiredFieldsMissing = fields.some(
        (field) => field.required && (!initializedValues[field.name] || initializedValues[field.name].trim() === '')
      );

      if (hasRequiredFieldsMissing) {
        onChange(null);
      } else {
        onChange(initializedValues);
      }
    }
  }, [fields]);

  // Run initial validation check on mount to catch any pre-existing validation issues
  useEffect(() => {
    const hasRequiredFieldsMissing = fields.some(
      (field) => field.required && (!values[field.name] || values[field.name].trim() === '')
    );

    if (hasRequiredFieldsMissing && fields.length > 0) {
      // Delay to ensure RJSF is ready
      setTimeout(() => {
        onChange(null);
      }, 0);
    }
  }, []); // Run only on mount

  const handleFieldChange = (fieldName: string, value: string) => {
    if (!dirty) setDirty(true);

    const newValues = {
      ...values,
      [fieldName]: value
    };

    setValues(newValues);

    // Check if there are missing required fields
    const hasRequiredFieldsMissing = fields.some(
      (field) => field.required && (!newValues[field.name] || newValues[field.name].trim() === '')
    );

    // Mark as dirty for validation display purposes
    setDirty(true);

    // If required fields are missing, pass null to trigger RJSF validation
    // Otherwise pass the values normally
    if (hasRequiredFieldsMissing) {
      onChange(null);
    } else {
      onChange(newValues);
    }
  };

  const getFieldError = (field: CompoundField) => {
    // Don't show individual field errors anymore - we'll show them at the title level
    return false;
  };

  const getFieldHelperText = (field: CompoundField) => {
    // No individual field helper text since errors are shown at title level
    return '';
  };

  const hasAnyErrors = () => {
    if (!dirty) return false;
    if (rawErrors && rawErrors.length > 0) return true;
    return fields.some((field) => field.required && (!values[field.name] || values[field.name].trim() === ''));
  };

  const getMissingRequiredFields = () => {
    if (!dirty) return [];
    return fields
      .filter((field) => field.required && (!values[field.name] || values[field.name].trim() === ''))
      .map((field) => field.placeholder);
  };

  const getTitleErrorMessage = () => {
    const missingFields = getMissingRequiredFields();
    if (missingFields.length === 0) return '';

    if (missingFields.length === 1) {
      return ` ${missingFields[0]} required`;
    } else if (missingFields.length === 2) {
      return ` ${missingFields.join(' and ')} required`;
    } else {
      const lastField = missingFields.pop();
      return ` ${missingFields.join(', ')}, and ${lastField} required`;
    }
  };

  const formatDisplayValue = (value: CompoundTextValue) => {
    if (!value) return '';

    const nonEmptyFields = fields
      .filter((field) => value[field.name] && value[field.name].trim() !== '')
      .map((field) => `${field.placeholder}: ${value[field.name]}`)
      .join(', ');

    return nonEmptyFields || 'No data entered';
  };

  // Debug info if no fields are configured
  if (!fields || fields.length === 0) {
    return (
      <div style={{ padding: '10px', border: '2px solid orange', backgroundColor: '#fff3cd' }}>
        <strong>CompoundTextInput:</strong> No fields configured.
        <br />
        <small>Field: {props.name || props.id}</small>
        <br />
        <small>Configure fields in ui:options.fields array</small>
      </div>
    );
  }

  if (props.readonly) {
    if (props.formContext?.hideEmpty && !formValue) return null;

    return (
      <div>
        <DetailRow
          key={props.id}
          colId={props.id}
          title={props.uiSchema?.['ui:placeholder'] || props.uiSchema?.['ui:title'] || ''}
          value={formatDisplayValue(formValue)}
        />
      </div>
    );
  }

  return (
    <Box width="100%">
      {/* Title with inline error message */}
      {props.uiSchema?.['ui:title'] && (
        <label
          htmlFor={props.id}
          className={hasAnyErrors() ? 'text-red-600' : 'text-gray-700'}
          style={{ marginBottom: '0.5rem', display: 'block' }}>
          {props.uiSchema?.['ui:title']}
          {hasAnyErrors() && (
            <span
              style={{
                color: '#dc2626',
                fontSize: '0.75rem',
                fontWeight: '400',
                marginLeft: '0.5rem'
              }}>
              {getTitleErrorMessage()}
            </span>
          )}
        </label>
      )}

      {/* Fields arranged in two-column grid */}
      <Grid
        container
        spacing={2}>
        {fields.map((field, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            key={field.name}>
            <TextField
              fullWidth
              value={values[field.name] || ''}
              required={field.required}
              error={getFieldError(field)}
              helperText={getFieldHelperText(field)}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              onBlur={() => setDirty(true)}
              label={field.placeholder}
              size={props.uiSchema?.['ui:size'] || 'small'}
              type={field.type || 'text'}
              InputLabelProps={{ style: { backgroundColor: 'white', paddingRight: '.5rem' } }}
              InputProps={{
                style: { fontSize: '.875rem' },
                name: field.name
              }}
              className={`${INPUT_STYLE} ${props.uiSchema?.classNames || ''}`}
            />
          </Grid>
        ))}
      </Grid>

      {/* Global error message if needed */}
      {hasAnyErrors() && props.rawErrors && props.rawErrors.length > 0 && (
        <Box
          mt={1}
          fontSize="0.75rem"
          color="error.main">
          Please complete all required fields
        </Box>
      )}
    </Box>
  );
};

export default CompoundTextInput;
